import {
  COUNTRY_DIAL_CODES,
  DEFAULT_COUNTRY_CODE,
} from "@/constants/countryCodes";

/**
 * A lead's WhatsApp number WITH its country code (owner, 2026-10-08: "on the
 * WhatsApp in view lead we need country code also").
 *
 * The WhatsApp number's country code is its OWN — never the lead's phone
 * code (owner, 2026-10-25: "the country code for phone and whatsapp should be
 * independent"). `Leads.whatsapp` is ONE free-text column (no code column,
 * unlike `phone` + `phoneCountryCode`); the pickers save "+<cc> <national>",
 * so a number edited in Xale always carries its code. A value without one
 * (older rows, raw answers from some sources) is read from the number alone:
 * the default country (+91) only when it is plainly an Indian mobile — the
 * same default the server applies to a bare WhatsApp number — and otherwise
 * shown exactly as stored. Changing the phone's code can no longer move it.
 *
 * Stored shapes and how they render:
 *   "+44 7123456789" / "+447123456789"   → "+44 7123456789"  (own code, split
 *                                           by longest known dial-code prefix)
 *   "0044 7123456789"                    → "+44 7123456789"  (00 = `+`)
 *   "9876543210"                         → "+91 9876543210"  (an Indian mobile:
 *                                           10 digits from 6–9; DISPLAY ONLY,
 *                                           the stored value is never rewritten)
 *   "919876543210"                       → "+91 9876543210"  (the same with its
 *                                           code written inline)
 *   "0523278916"                         → "0523278916"      (leading trunk 0:
 *                                           a guessed code would print a number
 *                                           that was never theirs — the owner's
 *                                           2026-10-04 phone rule)
 *   "501234567"                          → "501234567"       (not an Indian
 *                                           mobile: no code is invented)
 *
 * Saving from the picker writes `joinWhatsapp(code, national)` =
 * "+<cc> <national>" back into the same column, so the mobile app, exports
 * and the server's search / duplicate check all see the code at once.
 */
export type SplitWhatsapp = {
  countryCode: string;
  national: string;
  /** True when the code is known from the number itself (its `+cc` / `00`
   *  prefix, or the shape of an Indian mobile); false when the picker simply
   *  opens on the default code for a number that does not say its own. */
  explicit: boolean;
};

const DIAL_CODES_LONGEST_FIRST: string[] = [...COUNTRY_DIAL_CODES]
  .map((c) => c.code.replace(/[^\d]/g, ""))
  .filter(Boolean)
  .sort((a, b) => b.length - a.length || a.localeCompare(b));

const normalizeCode = (raw: string | null | undefined): string => {
  const digits = (raw ?? "").toString().replace(/[^\d]/g, "");
  return digits ? `+${digits}` : "";
};

/** Longest known dial-code prefix of an international digit string, or "". */
function matchDialCode(intlDigits: string): string {
  return DIAL_CODES_LONGEST_FIRST.find((cc) => intlDigits.startsWith(cc)) ?? "";
}

/** An Indian mobile number: 10 digits starting 6–9. */
const INDIAN_MOBILE = /^[6-9]\d{9}$/;

/**
 * The stored WhatsApp value as `{ countryCode, national }` for the pickers,
 * or null when there is nothing stored. A value that does not carry a code
 * of its own opens on `DEFAULT_COUNTRY_CODE` (never the phone's code), with
 * `explicit: false`.
 */
export function splitStoredWhatsapp(
  whatsapp: string | number | null | undefined,
): SplitWhatsapp | null {
  if (whatsapp == null) return null;
  const raw = String(whatsapp).trim();
  if (!raw) return null;

  const fallback = DEFAULT_COUNTRY_CODE;
  const compact = raw.replace(/[^\d+]/g, "");
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return { countryCode: fallback, national: "", explicit: false };

  // Own code: "+<cc><national>" or the "00" international prefix.
  const intl = compact.startsWith("+")
    ? compact.slice(1).replace(/[^\d]/g, "")
    : compact.startsWith("00")
      ? compact.slice(2)
      : "";
  if (intl) {
    const cc = matchDialCode(intl);
    if (cc && intl.length > cc.length) {
      return { countryCode: `+${cc}`, national: intl.slice(cc.length), explicit: true };
    }
    // Unknown prefix: keep every digit, let the user pick the code.
    return { countryCode: fallback, national: intl, explicit: false };
  }

  // No "+": an Indian mobile is recognisable by its own shape — bare, or
  // with its 91 written inline ("919876543210").
  if (INDIAN_MOBILE.test(digits)) {
    return { countryCode: "+91", national: digits, explicit: true };
  }
  if (digits.length === 12 && digits.startsWith("91") && INDIAN_MOBILE.test(digits.slice(2))) {
    return { countryCode: "+91", national: digits.slice(2), explicit: true };
  }

  return { countryCode: fallback, national: digits, explicit: false };
}

/**
 * The ONE display rule for a lead's WhatsApp number: `+91 9876543210`.
 * Returns the stored text untouched when the number does not say its own
 * code (see the table above) and "" for an empty value.
 */
export function formatLeadWhatsapp(whatsapp: string | number | null | undefined): string {
  if (whatsapp == null) return "";
  const raw = String(whatsapp).trim();
  if (!raw) return "";
  const split = splitStoredWhatsapp(raw);
  if (!split || !split.national || !split.explicit) return raw;
  return `${split.countryCode} ${split.national}`;
}

/** What the pickers save: "+<cc> <national>", or "" when the number is empty. */
export function joinWhatsapp(countryCode: string, national: string): string {
  const n = (national ?? "").replace(/[^\d]/g, "");
  if (!n) return "";
  const cc = normalizeCode(countryCode) || DEFAULT_COUNTRY_CODE;
  return `${cc} ${n}`;
}
